import { render } from 'preact';
import type { Capability } from '@clawharbor/manifest';
import { resolveSlot, safeHref, slotText, stringify, summarize } from '@clawharbor/render-core';
import type { Row } from '@clawharbor/render-core';
import { AppView } from './app.js';
import type { AppActions } from './app.js';
import { callCapability, fetchManifest, resolveTenant } from './dataplane.js';
import { Store } from './store.js';
import type { ResultOrigin, ResultRecord } from './store.js';
import { applyTheme } from './theme.js';
import { compactValues, defaultValues } from './ui/controls.js';
import type { FormValues } from './ui/controls.js';
import { observeToolChanges, registerCapabilities } from './webmcp.js';
import type { HostOutcome, Registration, WebMcpHost } from './webmcp.js';

/**
 * Wiring.
 *
 * One structural rule: the agent path and the human path call the same `run()`.
 * Anything else and an agent's tool call would update a shadow copy of the page
 * instead of the page — which on WebMCP means the tool did nothing observable,
 * because the page repaint is the only output the API can produce.
 */

export interface BootHandle {
  store: Store;
  dispose(): void;
}

export async function boot(container: HTMLElement): Promise<BootHandle> {
  const tenant = resolveTenant();
  const store = new Store(tenant);

  /** Form values, keyed by capability id. Not in the store: they are view state. */
  const forms: Record<string, FormValues> = {};
  let registration: Registration | null = null;
  let stopToolWatch: (() => void) | null = null;
  let bootController = new AbortController();

  const paint = (): void => {
    render(AppView({ state: store.state, forms, actions, now: new Date() }), container);
  };

  /**
   * The shared execution path.
   *
   * `origin` is carried so the page can show WHICH side triggered a result. That
   * visible attribution is what makes an agent call provable in a demo where the
   * host's own UI shows nothing at all.
   */
  async function run(
    cap: Capability,
    rawInput: FormValues,
    origin: ResultOrigin,
    signal?: AbortSignal,
  ): Promise<HostOutcome> {
    const input = compactValues(rawInput);
    store.setPending(cap.id, true);
    const started = performance.now();

    try {
      const outcome = await callCapability(store.state.tenant, cap.id, input, signal);
      const durationMs = Math.round(performance.now() - started);

      const summary =
        outcome.summary ??
        summarize(cap, outcome.result, {
          view: store.view(cap.view),
          locale: store.state.manifest?.identity.locale,
        });

      const record: ResultRecord = {
        capabilityId: cap.id,
        view: cap.view,
        result: outcome.result,
        summary,
        input,
        receivedAt: Date.now(),
        durationMs,
        origin,
      };
      if (outcome.deepLink) record.deepLink = outcome.deepLink;

      // THE REPAINT. Happens before this function resolves, so the agent's
      // spoken answer and the page the human is looking at land together.
      store.applyResult(record);
      if (cap.kind === 'stage') stageFrom(cap, input, record);

      store.log({
        name: cap.name,
        origin,
        ok: true,
        durationMs,
        at: Date.now(),
        count: outcome.result.rows.length,
      });

      const result: HostOutcome = { result: outcome.result, summary };
      if (outcome.deepLink) result.deepLink = outcome.deepLink;
      return result;
    } catch (err) {
      const durationMs = Math.round(performance.now() - started);
      store.log({
        name: cap.name,
        origin,
        ok: false,
        durationMs,
        at: Date.now(),
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    } finally {
      store.setPending(cap.id, false);
    }
  }

  /**
   * A `stage` result becomes an on-page proposal.
   *
   * Deliberately generic — it reads the first returned row and the view's own
   * slot bindings rather than knowing anything about cinemas — because the
   * manifest is the contract and this file must not grow tenant knowledge.
   */
  function stageFrom(cap: Capability, input: FormValues, record: ResultRecord): void {
    const row: Row = record.result.rows[0] ?? {};
    stageRow(cap, row, record.deepLink, input);
  }

  function stageRow(
    cap: Capability,
    row: Row,
    deepLink: string | undefined,
    input: FormValues = {},
  ): void {
    const view = store.view(cap.view);

    // Read the label through the view's own slot binding, so the staged panel
    // names the thing the same way the list that produced it did.
    const label =
      slotText(view?.slots['primary'] ?? view?.slots['title'], row) ||
      stringify(row['label'] ?? row['title'] ?? row['name']) ||
      cap.title;

    const href = deepLink ?? safeHref(resolveSlot(view?.linkField, row) ?? row['url']) ?? undefined;

    store.stage({
      label,
      ...(href ? { deepLink: href } : {}),
      stagedAt: new Date().toISOString(),
      fields: { ...input, ...row },
    });
  }

  const actions: AppActions = {
    selectCapability(id) {
      store.setActiveCapability(id);
    },
    setField(capabilityId, name, value) {
      forms[capabilityId] = { ...(forms[capabilityId] ?? {}), [name]: value };
      paint();
    },
    submit(capabilityId) {
      const cap = store.capability(capabilityId);
      if (!cap) return;
      void run(cap, forms[capabilityId] ?? {}, 'human').catch(() => {
        /* already logged; the debug strip shows the failure */
      });
    },
    reset(capabilityId) {
      const cap = store.capability(capabilityId);
      forms[capabilityId] = cap ? defaultValues(cap) : {};
      paint();
    },
    selectRow(capabilityId, row) {
      const cap = store.capability(capabilityId);
      if (cap) stageRow(cap, row, undefined);
    },
    clearStaged() {
      store.clearStaged();
    },
    retryBoot() {
      void load();
    },
  };

  /**
   * Held so `dispose()` can drop it.
   *
   * Without this the handle's teardown emptied the container and then left the
   * subscription live, so the very next store mutation — an in-flight call
   * landing after teardown — re-rendered the whole app into a container the
   * caller had already given up on. Measured: `dispose()` during a 1.5s tool
   * call resurrected the page 2s later, `[agent]`-attributed, for a tool that
   * no longer existed.
   */
  const stopPainting = store.subscribe(paint);
  paint();

  /**
   * The bridge handed to the WebMCP layer.
   *
   * This one method is the ENTIRE contract between the spec-fragile file and the
   * rest of the app: `webmcp.ts` knows nothing about fetching, state or the DOM.
   */
  const webmcpHost: WebMcpHost = {
    run: (cap, input, signal) => run(cap, input as FormValues, 'agent', signal),
  };

  async function load(): Promise<void> {
    bootController.abort();
    bootController = new AbortController();

    try {
      const manifest = await fetchManifest(tenant, bootController.signal);
      applyTheme(manifest);
      for (const cap of manifest.capabilities) forms[cap.id] ??= defaultValues(cap);
      store.setManifest(manifest);

      // Re-registering means tearing the old set down first — and the only way
      // to unregister a WebMCP tool is to abort the signal it was given. The
      // watcher goes first, or it reports the momentary empty tool set as the
      // page's real state. (Verified: the abort is visible to `getTools()`
      // immediately, so re-registering the same names on the next line is safe —
      // it is only a DUPLICATE LIVE name that raises InvalidStateError.)
      stopToolWatch?.();
      stopToolWatch = null;
      registration?.dispose();
      registration = await registerCapabilities(manifest, webmcpHost);
      store.setWebMcp({
        available: registration.available,
        source: registration.source,
        registered: registration.toolNames.length,
        toolNames: registration.toolNames,
        ...(registration.errors.length ? { error: registration.errors.join('; ') } : {}),
      });

      stopToolWatch = observeToolChanges((names) => {
        // Trust the user agent's own view over ours — it is what the agent sees.
        store.setWebMcp({ registered: names.length, toolNames: names });
      });
    } catch (err) {
      if (bootController.signal.aborted) return;
      store.fail(err instanceof Error ? err.message : String(err));
    }
  }

  await load();

  return {
    store,
    dispose() {
      // Order matters. Stop listening BEFORE aborting, so a late result from a
      // call that is still in flight cannot repaint a container the caller has
      // already reclaimed.
      stopPainting();
      stopToolWatch?.();
      bootController.abort();
      registration?.dispose();
      render(null, container);
    },
  };
}
